import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Percent,
  DollarSign,
  Users,
  Edit,
  Trash2,
  Loader2,
  Building2,
} from 'lucide-react';
import { campaignService } from '../services';
import { Campaign } from '../types';

export const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadCampaign(parseInt(id));
    }
  }, [id]);

  const loadCampaign = async (campaignId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.getCampaignById(String(campaignId));
      setCampaign(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!campaign) return;
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await campaignService.deleteCampaign(String(campaign.id));
        navigate('/campaigns');
      } catch (err: any) {
        setError(err.message || 'Failed to delete campaign');
      }
    }
  };

  const getStatusVariant = (active: boolean): 'default' | 'secondary' => {
    return active ? 'default' : 'secondary';
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Campaigns
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error || 'Campaign not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/campaigns')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{campaign.title}</h1>
            <p className="text-muted-foreground">Campaign Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(campaign.active)}>
            {campaign.active ? 'Active' : 'Inactive'}
          </Badge>
          <Button variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Campaign Information */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Building2 className="h-4 w-4" />
                Branch
              </div>
              <p className="text-base">{campaign.branch?.name || '-'}</p>
              <p className="text-sm text-muted-foreground">{campaign.branch?.address || ''}</p>
            </div>

            {campaign.description && (
              <>
                <Separator />
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Description</div>
                  <p className="text-base">{campaign.description}</p>
                </div>
              </>
            )}

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Campaign Period
              </div>
              <p className="text-base">
                {campaign.startDate ? new Date(campaign.startDate).toLocaleDateString() : '-'} -{' '}
                {campaign.endDate ? new Date(campaign.endDate).toLocaleDateString() : '-'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Commission Details */}
        <Card>
          <CardHeader>
            <CardTitle>Commission Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Percent className="h-4 w-4" />
                Additional Commission Rate
              </div>
              <p className="text-2xl font-bold">{campaign.additionalCommissionRate}%</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                Additional Fixed Commission
              </div>
              <p className="text-2xl font-bold">${campaign.additionalFixedCommission}</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Minimum People Required
              </div>
              <p className="text-base">{campaign.minPeopleRequired} people</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Campaign Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Created At
              </div>
              <p className="text-base">{new Date(campaign.createdAt).toLocaleString()}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Last Updated
              </div>
              <p className="text-base">{new Date(campaign.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignDetailPage;
